import React, { useState, useEffect } from 'react';

const Solutions = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = "Farmnet's disease surveillance helps detect crop threats early—protecting yields and farmer incomes with real-time alerts and expert advice.";

  useEffect(() => {
    let currentIndex = 0;
    let typingInterval;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setIsTyping(false);
          setTimeout(() => {
            backspaceText();
          }, 2000);
        }, 1000);
      }
    };

    const backspaceText = () => {
      let backspaceInterval;
      let backspaceIndex = fullText.length;

      const eraseText = () => {
        if (backspaceIndex >= 0) {
          setText(fullText.substring(0, backspaceIndex));
          backspaceIndex--;
        } else {
          clearInterval(backspaceInterval);
          setIsTyping(true);
          currentIndex = 0;
          typingInterval = setInterval(typeText, 50);
        }
      };

      backspaceInterval = setInterval(eraseText, 20);
    };

    if (isTyping) {
      typingInterval = setInterval(typeText, 50);
    }

    return () => {
      clearInterval(typingInterval);
    };
  }, [isTyping, fullText]);

  return (
    <div className="relative bg-[#F3F3F3]">
      {/* Content */}
      <div className="relative z-10 px-4 md:px-24 pb-10 md:pt-5 pt-1">
        {/* Heading */}
        <div className="mt-2 md:mt-2 md:mb-5 flex justify-between items-center p mb-2">
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 ">
          <span className='text-[#152C24]'>INSIGHTS & INNOVATIONS</span>
          </h1>
          <div>
            <div className='flex md:gap-2 gap-1 justify-center md:mt-2'>
              <div className='bg-[#CFD7D7] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
              <div className="bg-[#2E453D] border-b-2 border-[#FFBD26] md:ml-0 ml-2 text-white font-normal md:px-6 px-2 rounded-md md:rounded-[30px] w-[70px] h-[15px] md:w-[150px] md:text-[20px] text-[6px] md:h-[40px] mt-0 md:-mt-[1px] flex justify-between items-center">
                See More
              </div>
              <div className='bg-[#2E453D] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 mr-2 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
            </div>
          </div>
        </div>

        {/* Records Section */}
        <div className="rounded-md w-full md:h-[630px] h-[175px] relative flex items-center justify-center p-2 md:p-0 pt-2 md:mt-4">
          <div className="rounded-xl overflow-hidden relative w-full h-[180px] md:mt-5 md:h-[700px] flex justify-center items-center cursor-pointer">
              <>
                <img
                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751380762/image-1_p0gfth.png"
                  alt="Mobile"
                  className="object-cover w-full h-full"
                />
              </>
          </div>
        </div>

        {/* Description */}
        <div className='md:text-[32px] text-[10px] font-normal md:mt-16 mt-2 space-y-6'>
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 text-[#979797]">
            INSIGHTS & INNOVATIONS | <span className='text-[#152C24]'>Disease Surveillance</span>
          </h1>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24] ">
            What is Disease Surveillance in Agriculture?
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            It is the systematic monitoring, detection, and reporting of crop diseases and pest outbreaks. Its goal is to:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc -mt-1 -md:mt-4 list-inside pl-3 font-bold">
            <li>Detect early signs of plant diseases</li>
            <li>Prevent large-scale crop damage</li>
            <li>Provide timely intervention and control measures</li>
            <li>Reduce the spread of pests and pathogens</li>
            <li>Improve food security and farmer incomes</li>
          </ul>
          
          <p className="md:text-[32px] text-[8px]">
            It involves gathering data from farms, analyzing disease patterns, and delivering actionable alerts to farmers and stakeholders.
          </p>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24] md:mb-3">
            How the Farmnet App Provides Disease Surveillance Services:
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            The Farmnet App helps farmers and agricultural partners stay ahead of crop diseases by using digital tools to monitor, report, and respond quickly to threats. Here's how it works:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-none pl-0 space-y-3">
  <li className="flex items-start">
    <span className="text-[#2E453D] font-bold mr-2">✅</span>
    <div>
      <span className="font-bold">Real-Time Disease Alerts:</span>
      <ul className="list-disc  mt-1">
        <li>Notifies farmers of emerging pest and disease outbreaks</li>
        <li>Based on field data, farmer reports, and satellite imagery</li>
        <li>Crop-specific alerts with symptom details</li>
        <li>Includes recommended actions</li>
      </ul>
    </div>
  </li>
  <li className="flex items-start">
    <span className="text-[#2E453D] font-bold mr-2">📷</span>
    <div>
      <span className="font-bold">Image-Based Diagnostics:</span>
      <ul className="list-disc  mt-1">
        <li>Farmers upload photos of affected crops</li>
        <li>AI-assisted tools or agronomists diagnose problems</li>
        <li>Provides treatment suggestions</li>
      </ul>
    </div>
  </li>
  <li className="flex items-start">
    <span className="text-[#2E453D] font-bold mr-2">📡</span>
    <div>
      <span className="font-bold">Crowdsourced Data Collection:</span>
      <ul className="list-disc  mt-1">
        <li>Aggregates data from farmers, extension workers, and field agents</li>
        <li>Maps disease hotspots</li>
        <li>Strengthens early warning systems</li>
      </ul>
    </div>
  </li>
  <li className="flex items-start">
    <span className="text-[#2E453D] font-bold mr-2">🌍</span>
    <div>
      <span className="font-bold">Location-Specific Monitoring:</span>
      <ul className="list-disc  mt-1">
        <li>Uses GPS to pinpoint disease risks</li>
        <li>Helps target interventions where most needed</li>
      </ul>
    </div>
  </li>
  <li className="flex items-start">
    <span className="text-[#2E453D] font-bold mr-2">📚</span>
    <div>
      <span className="font-bold">Guidance and Support:</span>
      <ul className="list-disc mt-1">
        <li>Disease life cycle information</li>
        <li>Preventive measures</li>
        <li>Organic and chemical treatment options</li>
        <li>Integrated pest management practices</li>
      </ul>
    </div>
  </li>
  <li className="flex items-start">
    <span className="text-[#2E453D] font-bold mr-2">🤝</span>
    <div>
      <span className="font-bold">Link to Experts:</span>
      <ul className="list-disc  mt-1">
        <li>Connect with agronomists</li>
        <li>Access local extension officers</li>
        <li>Request farm visits</li>
        <li>Get personalized advice</li>
      </ul>
    </div>
  </li>
</ul>
        <div className="-mt-5">

    <div>
    <h3 className="text-[12px] md:text-[32px] font-bold text-[#152C24] ">
            Why It Matters:
          </h3>
          
          <p className="md:text-[32px] text-[8px] ">
            With Farmnet's disease surveillance services, farmers:
          </p>
          
    </div>
        <ul className="md:text-[32px] text-[8px] list-disc list-inside pl-3 font-bold">
            <li>Act quickly to prevent crop losses</li>
            <li>Reduce costs from delayed or unnecessary treatments</li>
            <li>Farm more sustainably and profitably</li>
          </ul>
        </div>
        </div>
        
        <div className='flex justify-end items-center'>
          <div>
            <h1 className="text-[18px] md:text-[32px] font-bold mt-2 mb-4 text-black">
              Download App
            </h1>
            <div className="md:w-[450px]">
              <div className="flex flex-row justify-between space-x-2 items-center sm:space-y-0">
                <a href="https://play.google.com/store/apps/details?id=com.farmnet.africa&pcampaignid=web_share" alt="none">   
             <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751889690/google_PLAY-LOGO.WINE_emyxpu.png" className="h-[25px] md:w-[280px] md:h-[180px]" alt="App Store" />    
             <p className="md:text-[14px] text-[6px] md:-mt-12 md:pl-5 font-normal md:px-1">
             Click to download App
  </p>
     </a>
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744414874/image_scynki.png"
                  alt="Google Play"
                  className="h-[30px] md:h-[75px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
        <div className='md:h-10 h-5'></div>
      </div>
    </div>
  );
};

export default Solutions;